import React, {
  FunctionComponent,
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Noun, Verb } from '@trello/analytics';
import {
  Source,
  Action,
  ActionSubject,
  ActionSubjectId,
} from '@trello/atlassian-analytics';
import { idToDate } from '@trello/dates';
import { featureFlagClient } from '@trello/feature-flag-client';
import { useEmailHygieneWizardQuery } from '@trello/graphql/generated';
import { currentLocale } from '@trello/locale';
import Banner from 'app/common/components/banner/banner';
import Icon, { IconColor, IconName } from 'app/common/components/controls/icon';
import { forNamespace } from 'app/common/lib/util/i18n';
import {
  Overlay,
  OverlayAlignment,
  OverlayBackground,
} from 'app/src/components/Overlay';
import { track } from './Analytics';
import styles from './EmailHygieneBanner.less';

const EmailHygieneWizardPromise = import(
  /* webpackChunkName: "email-hygiene-wizard" */ './EmailHygieneWizard'
);
const EmailHygieneWizard = lazy(() => EmailHygieneWizardPromise);

let analyticsContext: object;

const format = forNamespace(['email hygiene wizard', 'banner'], {
  shouldEscapeStrings: false,
});

export const DISMISSABLE_MESSAGE_ID = 'primary-email-hygiene';

export interface EmailHygieneBannerProps {
  forceShow?: boolean;
}

const updateByDate = new Date(2020, 1, 19).toLocaleDateString(currentLocale, {
  month: 'long',
  day: 'numeric',
});

export const EmailHygieneBanner: FunctionComponent<EmailHygieneBannerProps> = ({
  forceShow,
}) => {
  const [shouldShowHygiene, setShouldShowHygiene] = useState(false);
  const [isOverlayOpen, setOverlayOpen] = useState(true);

  const dataHook = useEmailHygieneWizardQuery({
    variables: { memberId: 'me' },
  });

  const me = dataHook.data && dataHook.data.member;

  useEffect(() => {
    const oldestLogin = me?.logins?.sort((a, b) => b.id.localeCompare(a.id))[0];
    const timeDiffInDays =
      oldestLogin &&
      (Date.now() - idToDate(oldestLogin.id).valueOf()) / 1000 / 60 / 60 / 24;
    const shouldShow =
      !!me &&
      (/[?&]forceHygiene=true/i.test(window.location.search) ||
        forceShow ||
        (me.confirmed &&
          (!timeDiffInDays || timeDiffInDays > 7) &&
          !me.isAaMastered &&
          !me.aaEnrolledDate &&
          me.logins &&
          me.logins.some((login) => login.claimable) &&
          (!me.oneTimeMessagesDismissed ||
            me.oneTimeMessagesDismissed.indexOf(DISMISSABLE_MESSAGE_ID) < 0) &&
          featureFlagClient.get('aa4a.show-email-hygiene', false)));

    if (shouldShow) {
      setShouldShowHygiene(true);
    }
  }, [me, forceShow]);

  const onClickConfirmEmail = useCallback(() => {
    dataHook.refetch();
    setOverlayOpen(true);
    track({
      verb: Verb.INITIATES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'by clicking banner link',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: Action.clicked,
      actionSubject: ActionSubject.link,
      actionSubjectId: ActionSubjectId.emailHygieneBannerLink,
    });
  }, [dataHook]);

  const closeOverlayImplicitly = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.DISMISSES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'with an implicit input',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: Action.closed,
      actionSubject: ActionSubject.overlay,
      actionSubjectId: ActionSubjectId.emailHygieneWizard,
    });
  }, []);

  const closeOverlay = useCallback(() => {
    setOverlayOpen(false);
    track({
      verb: Verb.DISMISSES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'by manually closing the dialog',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: Action.closed,
      actionSubject: ActionSubject.overlay,
      actionSubjectId: ActionSubjectId.emailHygieneWizard,
    });
  }, []);

  const completeProcess = useCallback(() => {
    setShouldShowHygiene(false);
    track({
      verb: Verb.COMPLETES,
      noun: Noun.EMAIL_HYGIENE_WIZARD,
      method: 'by dismissing the final screen',
      context: analyticsContext,
      source: Source.emailHygieneBanner,
      action: Action.closed,
      actionSubject: ActionSubject.overlay,
      actionSubjectId: ActionSubjectId.emailHygieneWizard,
    });
  }, []);

  const logins = me && Array.from(me.logins);

  // sort by two criteria:
  //   1. primary (to make sure that the current primary email address is on top)
  //   2. ID desc (so that the most recently added addresses will be the highest)
  if (logins) {
    logins.sort(
      (a, b) =>
        (b.primary ? 1 : 0) - (a.primary ? 1 : 0) || b.id.localeCompare(a.id),
    );
  }

  if (!analyticsContext) {
    analyticsContext = {
      enterpriseId: me && me.idEnterprise,
      emailCount: logins && logins.length,
      claimableEmailCount: logins && logins.filter((l) => l.claimable).length,
    };
  }

  return !shouldShowHygiene || !me || !logins ? null : (
    <div>
      <Banner className={styles.banner}>
        <p className={styles.text}>
          <Icon
            name={IconName.Information}
            color={IconColor.Yellow}
            large={true}
          ></Icon>
          {format('main-message', { updateByDate })}
          <a
            href="#"
            className={styles.updateLink}
            onClick={(e) => {
              e.preventDefault();
              onClickConfirmEmail();
            }}
          >
            {format('link-text')}
          </a>
        </p>
        {isOverlayOpen && (
          <Overlay
            background={OverlayBackground.LIGHT}
            onClose={closeOverlayImplicitly}
            alignment={OverlayAlignment.TOP}
          >
            <Suspense fallback={null}>
              <EmailHygieneWizard
                logins={logins}
                name={me.fullName || me.username}
                avatarUrl={me.avatarUrl || undefined}
                initials={me.initials || undefined}
                updateByDate={updateByDate}
                onDismiss={closeOverlay}
                analyticsContext={analyticsContext}
                onComplete={completeProcess}
              />
            </Suspense>
          </Overlay>
        )}
      </Banner>
    </div>
  );
};
